import { css } from 'styled-components';
import { TachyonsSpacing } from './tachyons-spacing';
import { TachyonsDisplay } from './tachyons-display';
import {
  mqSmallMin,
  mqTabletMax,
  mqLargeMin,
} from 'styled/tools/media-queries';
import {
  Ellipsis,
  HideScrollbar,
  SiteButton2,
  SiteButtonHover2,
  WithFontBody,
  WithFontHead,
  WithSiteBleedLayout,
  WithSiteBleedScreen,
  WithSiteLayoutPadding,
  WithSiteLayoutWidth,
} from 'styles/commonCss';

const mainWidth = '645px';

export const PageReference = css`
  .page-reference,
  .page-new-sustainability {
    .site-content {
      .white-text {
        * {
          color: #fff;
        }
      }
      * {
        color: var(--text-primary);
        font-family: var(--flag-font-family);
      }

      > .container {
        padding-top: 70px;
        padding-bottom: 70px;
        @media ${mqLargeMin} {
          padding-top: 160px;
        }
      }

      .sections-list {
        position: relative;
        display: block;

        > section {
          width: ${mainWidth};
          max-width: 100%;
          margin: 0 auto;
          display: block;

          h1,
          h2 {
            ${WithFontHead}
            margin: 50px 0 0;
            @media ${mqLargeMin} {
              margin: 100px 0 0;
            }

            letter-spacing: var(--flag-letter-spacing-1);
            @media ${mqSmallMin} {
              letter-spacing: var(--flag-letter-spacing-2);
            }
          }
          h3,
          h4,
          h5,
          h6 {
            ${WithFontHead}
            margin: 35px 0 0;
            font-size: 26px;

            @media ${mqLargeMin} {
              font-size: var(--font-size-800);
            }
          }

          p {
            ${WithFontBody}
            margin: 35px 0 0;
            font-size: var(--font-size-400);
          }

          strong,
          b {
            ${WithFontBody}
            font-weight: 500;
          }
          b i {
            font-weight: 500;
          }

          ul {
            list-style-image: url(https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/w_9,h_9/favicon);
            list-style-position: outside;
            padding: 0 0 0 18px;
            margin: 35px 0 0;
          }
          ul li {
            margin: 35px 0 0;
            padding: 0 0 0 15px;
            @media ${mqLargeMin} {
              padding: 0 0 0 30px;
            }
            &:first-child {
              margin: 0;
            }
          }
          ul li * {
            margin: 0;
          }

          ol {
            list-style-position: outside;
            list-style-type: decimal;
            padding: 0 0 0 10px;
            margin: 35px 0 0;
          }
          ol li {
            margin: 5px 0 0;
            &:first-child {
              margin: 0;
            }
          }
          ol li * {
            margin: 0;
          }

          ol ol {
            padding: 0 0 0 20px;
            list-style-type: upper-alpha;
          }

          .bleed-screen {
            width: 100%;
            @media ${mqLargeMin} {
              width: calc(100% + ((100vw - 100%) / 2));
            }
          }
          .bleed-layout {
            width: 100%;
            @media ${mqLargeMin} {
              width: 992.5px;
            }
          }

          [data-type='small'] {
            ${WithFontBody}
            display: block;
          }
          [data-type='small'] > * {
            margin: 10px 0 0;
            &:first-child {
              margin: 0;
            }
          }
          [data-type='small'] * {
            font-size: var(--font-size-300);
          }

          [data-type='table'] {
            margin: 35px 0 0;
            table {
              border-color: var(--outline-neutral);
              border-collapse: collapse;
              width: 100%;
              * {
                border-color: var(--outline-neutral);
              }
            }
            td {
              vertical-align: top;
              font-size: var(--font-size-300);
              @media ${mqLargeMin} {
                font-size: var(--font-size-400);
              }
              * {
                font-size: var(--font-size-300);
                @media ${mqLargeMin} {
                  font-size: var(--font-size-400);
                }
              }
            }
            &.with-layout-fixed {
              table {
                table-layout: fixed;
                width: 100%;
              }
            }
            &.with-no-border {
              table {
                border: 0;
              }
              tr {
                border: 0;
              }
              td {
                border: 0;
                padding-top: 0;
                vertical-align: top;
                padding-bottom: 15px;
                @media ${mqLargeMin} {
                  padding-bottom: 0;
                }
                &:first-of-type {
                  padding-right: 10px;
                }
              }
            }
            &.with-tr-border {
              tr {
                border-width: 1px;
                border-style: solid;
              }
            }
            &.with-tr-dashed {
              tr {
                border-style: dashed;
                border-width: 1px;
                border-right: 0px;
                border-left: 0px;
              }
            }
            &.with-td-border {
              td {
                border-width: 1px;
                border-style: solid;
              }
            }
            &.with-td-dashed {
              td {
                border-width: 1px;
                border-style: dashed;
              }
            }
            &.with-td-pa2 {
              td {
                padding: var(--spacing-8);
              }
            }
            &.with-td-pv2 {
              td {
                padding-top: var(--spacing-8);
                padding-bottom: var(--spacing-8);
              }
            }
            &.with-td-pa3 {
              td {
                padding: var(--spacing-24);
              }
            }
            &.with-td-pv3 {
              td {
                padding-top: var(--spacing-24);
                padding-bottom: var(--spacing-24);
              }
            }
            &.bleed-layout {
              ${WithSiteBleedLayout(645)}
            }
            &.bleed-screen {
              ${WithSiteBleedScreen}
            }
            &.with-bullets {
              display: flex;
              flex-direction: column;
              @media ${mqLargeMin} {
                display: none;
              }
              table {
                ${HideScrollbar}
                display: block;
                overflow-x: auto;
                white-space: nowrap;
                @media ${mqLargeMin} {
                  display: table;
                  white-space: initial;
                }
              }
              .bullets {
                display: flex;
                justify-content: center;
                margin-top: 30px;
                .bullet {
                  margin-right: 7px;
                  width: 5px;
                  height: 5px;
                  border-radius: 50%;
                  background-color: var(--fill-neutral-light);
                  &:last-of-type {
                    margin-right: 0;
                  }
                  &.active {
                    background-color: var(--fill-brand);
                  }
                }
              }
            }
          }

          [data-type='poster'] {
            margin: 60px 0;
            @media ${mqLargeMin} {
              margin: 75px 0;
            }

            > .container {
              position: relative;
            }

            .label {
              display: block;
              margin: 15px 0 0;
              font-size: var(--font-size-300);
              @media ${mqLargeMin} {
                max-width: 495px;
              }
              i,
              span {
                font-size: var(--font-size-300);
              }
            }
            .label:empty {
              display: none;
            }

            &.with-row {
              .grid {
                width: 100%;
                .container {
                  display: flex;
                  flex-direction: row;
                  > div {
                    flex: 1;
                  }
                }
              }
            }
            &.bleed-screen {
              ${WithSiteBleedScreen}
            }
            &.bleed-layout {
              ${WithSiteBleedLayout(645)}
            }
            &.with-numeric {
              .numeric-wrap {
                position: relative;
                .numeric {
                  position: absolute;
                  top: 0;
                  font-size: 12px;
                  right: 0;
                }
              }
            }
            &.with-border-figure {
              > .container {
                border-bottom: 1px solid var(--outline-neutral);
                padding-bottom: 40px;
                margin-bottom: 15px;
              }
            }
            // GALLERY
            &:not([data-inline='true']) {
              .picture {
                background-size: cover;
                float: left;
                overflow: hidden;
                position: relative;
                border: 1px solid #fff;
                background-position: center;
                & > div {
                  height: 100%;
                }
                img {
                  display: none !important;
                }
              }

              .grid {
                display: block;
                position: relative;
                padding-bottom: 50%;
                margin: 0 -1px;
              }
              .grid > .container {
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                position: absolute;
              }

              .with-1 .picture {
                height: 100%;
                width: 100%;
              }
              .with-2 .picture {
                height: 100%;
                &:nth-child(1) {
                  width: 50%;
                }
                &:nth-child(2) {
                  width: 50%;
                }
              }
              .with-3 .picture {
                &:nth-child(1) {
                  width: 50%;
                  height: 100%;
                }
                &:nth-child(2) {
                  width: 50%;
                  height: 50%;
                }
                &:nth-child(3) {
                  width: 50%;
                  height: 50%;
                }
              }
              .with-4 .picture {
                &:nth-child(1) {
                  width: 25%;
                  height: 100%;
                }
                &:nth-child(2) {
                  width: 25%;
                  height: 100%;
                }
                &:nth-child(3) {
                  width: 50%;
                  height: 50%;
                }
                &:nth-child(4) {
                  width: 50%;
                  height: 50%;
                }
              }
            }
          }
          [data-type='poster'] + * {
            *:first-child:not(.swiper-pagination-bullet) {
              margin: 0;
            }
          }

          hr {
            border: 0;
            opacity: 0.2;
            display: block;
            border-top: 1px solid var(--outline-primary);
            position: relative;
            margin: 30px 0;
            @media ${mqLargeMin} {
              margin: 75px 0;
            }
            &:after {
              @media ${mqLargeMin} {
                content: '';
                position: absolute;
                width: calc((1440px - 100px - 100%) / 2);
                left: 100%;
                top: 0;
                border-top: 1px solid var(--outline-primary);
              }
            }
          }
          hr + * {
            margin: 0;
          }

          .info {
            background-color: var(--fill-secondary);
            padding: 30px;
            margin: 60px 0;
            @media ${mqLargeMin} {
              margin: 75px 0;
            }
            *:first-child {
              margin: 0;
            }
            & > *,
            a {
              font-weight: 500;
            }
            a {
              text-decoration: underline;
              cursor: pointer;
            }
          }
          .info + * {
            margin: 0;
          }

          .with-border {
            padding: var(--spacing-8);
            border: 1px solid var(--outline-neutral);
            @media ${mqLargeMin} {
              padding: 0;
              border: none;
            }
          }

          .with-2-columns {
            margin: 35px 0 0;
            @media ${mqSmallMin} {
              column-count: 2;
              column-gap: var(--spacing-24);
            }
            p {
              margin: 0;
            }
          }
          .with-3-columns {
            margin: 35px 0 0;
            @media ${mqSmallMin} {
              column-count: 3;
              column-gap: var(--spacing-24);
            }
            p {
              margin: 0;
            }
          }

          .text-columns {
            display: flex;
            align-items: flex-start;
            width: calc(50vw - ((50vw - 100%) / 2));
            @media ${mqTabletMax} {
              flex-direction: column;
              width: 100%;
            }
            & > * {
              width: 50%;
              margin-bottom: 0;
              @media ${mqTabletMax} {
                width: 100%;
              }
            }
            & > *:last-of-type {
              margin-left: 100px;
              @media ${mqTabletMax} {
                margin-left: 0;
              }
            }
          }

          .site-accordion {
            box-sizing: content-box;
            border-left: 0;
            border-right: 0;
            margin: 50px 0 0;
            @media ${mqLargeMin} {
              margin: 75px 0 0;
            }

            > .head {
              align-items: center;
              padding: var(--spacing-24) 0;

              .title {
                display: flex;
                flex-grow: 1;
                flex-direction: column;
                font-size: var(--font-size-400);
                padding: 0 35px 0 0;
                font-weight: 500;
                margin: 0;
                @media ${mqLargeMin} {
                  flex-direction: row;
                  align-items: center;
                  justify-content: space-between;
                }
                * {
                  font-size: var(--font-size-400);
                  font-weight: 500;
                  display: block;
                  margin: 0;
                }
                span {
                  font-size: var(--font-size-350);
                  font-weight: 500;
                  * {
                    margin: 0;
                    font-weight: 500;
                    display: block;
                  }
                  &:last-child {
                    font-size: var(--font-size-400);
                    font-weight: 400;
                    @media ${mqLargeMin} {
                      font-size: var(--font-size-350);
                    }
                  }
                  &:first-child {
                    width: 100%;
                    @media ${mqLargeMin} {
                      width: 80%;
                    }
                  }
                }
              }
            }
            > .body {
              .container {
                background: #fff;
                padding: 0 0 var(--spacing-24);
                > div > *:first-child {
                  margin: 0;
                }
                p {
                  margin: 0;
                }
              }
            }

            [data-type='table'] {
              margin: 0;
              .body {
                width: calc(100% + 30px);
                @media ${mqLargeMin} {
                  width: 100%;
                }
              }
              td {
                border: 0;
                padding: var(--spacing-24);
                padding-left: 0;
              }
              td:not(:empty) {
                border-top: 1px dashed var(--outline-neutral);
              }
              tr:first-child td {
                padding: var(--spacing-24) var(--spacing-8) var(--spacing-24) 0;
                font-weight: 500;
              }
              &.with-bullets {
                padding-bottom: 30px;
                .bullets {
                  width: calc(100% - 30px);
                  @media ${mqLargeMin} {
                    width: 100%;
                  }
                }
              }
            }
          }
          .site-accordion.active {
            > .head {
              .title {
                flex-grow: 1;
                position: relative;
                z-index: 1;
              }
            }
            > .body {
              > .container {
                border-top: 0;
              }
            }
          }
          .site-accordion + .site-accordion {
            margin: -1px 0 0;
            @media ${mqLargeMin} {
              margin: -1px 0 0;
            }
          }
          .site-accordion.with-head-th {
            .head {
              .title {
                @media ${mqLargeMin} {
                  transform: translateY(2px);
                }
              }
            }
            .body {
              max-height: 65px;
              visibility: hidden;
              margin-bottom: -65px;
            }
            &.active .body {
              visibility: visible;
              margin-bottom: 0;
            }
          }
          .site-accordion.with-head-th.active {
            .head {
              .title {
                @media ${mqLargeMin} {
                  opacity: 0;
                }
              }
            }
            .body {
              @media ${mqLargeMin} {
                margin-bottom: 0;
                visibility: visible;
              }
              .container {
                background-color: transparent;
                padding: 0;
              }
            }

            [data-type='table'] {
              tr:first-child td,
              tr:first-child td * {
                @media ${mqLargeMin} {
                  line-height: 1.5;
                  font-weight: 500;
                  border-color: transparent;
                }
              }
            }
          }

          .swiper-pagination {
            margin-top: var(--spacing-24);
          }

          .button {
            margin-top: 60px;
            @media ${mqLargeMin} {
              margin-top: 35px;
            }

            &.button-secondary {
              ${SiteButton2}
              ${SiteButtonHover2}
            }

            &.button-tertiary {
              display: -webkit-inline-box;
              display: -moz-inline-box;
              display: -webkit-inline-flex;
              display: -ms-inline-flexbox;
              display: inline-flex;
              background: transparent;
              text-transform: none;
              min-width: auto;
              border: none;
              height: auto;
              width: auto;
              padding: 0;

              i {
                margin: 0 var(--spacing-8) 0 0;
              }
            }
          }

          > .container {
            ${TachyonsSpacing({
              spacingLarge: '75px',
              spacingMedium: '35px',
              spacingExtraExtraLarge: '105px',
            })}
            ${TachyonsDisplay}
          }
        }
      }

      .max-width-poster {
        [data-type='poster'] {
          max-width: 500px;
        }
      }

      .sections-list {
        > section {
          margin-bottom: 100px;
          @media ${mqLargeMin} {
            margin-bottom: 0;
            p + p {
              margin-top: 20px;
            }
            p:first-of-type {
              margin-top: 30px;
            }
            &.hero {
              margin-bottom: 100px;
            }
          }
          &:last-of-type {
            margin-bottom: 0;
          }

          .swiper-pagination {
            margin-top: 35px;
          }

          [data-type='heading'],
          [data-type='paragraph'] {
            h2 {
              font-size: 40px;
              margin-top: 0;
              @media ${mqLargeMin} {
                font-size: 50px;
                margin-top: 100px;
              }
            }
            h3 {
              font-size: 26px;
              line-height: 31px;
              @media ${mqLargeMin} {
                font-size: 30px;
                line-height: 40px;
              }
            }
          }

          [data-type='poster'] {
            &.fig-2 {
              > .container {
                background-color: var(--fill-secondary);
                margin-bottom: var(--spacing-8);
                @media ${mqSmallMin} {
                  padding: 100px;
                }
              }
            }
            &.fig-5 {
              > .container {
                background-color: var(--fill-secondary);
                margin-bottom: var(--spacing-8);
                padding: var(--spacing-24);
                @media ${mqSmallMin} {
                  padding: 100px;
                }
              }
            }
          }

          [data-type='table'] {
            &[data-title='Prebiotic Pomegranate Extract small'] {
              width: calc(100% + 30px);
              td:not(:first-child) {
                border-left: 0;
                border-right: 0;
                min-width: 250px;
              }
              td:first-child {
                font-weight: 500;
              }
              td:last-child {
                border-right: 1px solid var(--outline-neutral);
              }
            }
            &[data-title='Quality Control Testing Battery large'] {
              tr:not(:first-child) td {
                padding-top: var(--spacing-8);
                padding-bottom: var(--spacing-8);
              }
              tr:last-child td {
                padding-bottom: var(--spacing-24);
              }
            }
            &[data-title='Quality Control Testing Battery small'] {
              padding-bottom: 0px;
              width: 100%;
              td {
                padding: var(--spacing-24) var(--spacing-8) !important;
                width: 40%;
              }
              td:first-child {
                padding-right: var(--spacing-8) !important;
                padding-left: 0 !important;
                width: 35%;
              }
              td:last-child {
                padding-right: 0 !important;
                padding-left: var(--spacing-8) !important;
                width: 25%;
              }
            }
            &[data-title='allergens--small'] {
              width: calc(100% + 30px);
              td:not(:first-child) {
                border-left: 0;
                border-right: 0;
                min-width: 180px;
              }
              td:first-child {
                font-weight: 500;
              }
              td:last-child {
                border-right: 1px solid var(--outline-neutral);
              }
            }
            &[data-title='probiotic-potency--small'],
            &[data-title='allergens--small'],
            &[data-title='chemical-contaminants-heavy-metals--small'],
            &[data-title='chemical-contaminants--small'],
            &[data-title='digestive-survivability--small'],
            &[data-title='thermostability--small'],
            &[data-title='whole-genome--sequencing--small'],
            &[data-title='water-activity--small'],
            &[data-title='Quality Control Testing Battery small'] {
              tr:last-of-type {
                border: none;
              }
            }

            &[data-title='benefit-specific-research--large'] {
              tr:not(:first-of-type) {
                td {
                  border: 1px solid #d2d7d0;
                }
              }
              td {
                &:first-of-type {
                  width: 35%;
                  min-width: 330px;
                }
                span {
                  display: block;
                }
              }
            }
          }

          &[data-title='Overview'] {
            p {
              padding-top: 0;
              font-size: 16px;
              line-height: 24px;

              @media ${mqLargeMin} {
                font-size: 30px;
                line-height: 40px;
                font-weight: 300;
              }
            }
          }

          &[data-title='Formulation'] {
            .site-accordion {
              > .head {
                .title {
                  padding: 0 100px 0 0;

                  span:last-child {
                    font-size: var(--font-size-300);
                    margin-top: 20px;
                    @media ${mqLargeMin} {
                      text-align: left;
                      min-width: 120px;
                      margin-top: 0;
                      font-size: var(--font-size-400);
                    }
                  }
                }
              }
              .body .container p {
                margin-bottom: 15px;
                &:last-of-type {
                  margin-bottom: 0;
                }
              }
            }
          }

          &[data-title='Testing'] {
            .site-accordion {
              .body .container p:not(:first-of-type) {
                margin: 35px 0 0;
              }

              [data-type='table'].lg {
                td {
                  min-width: 250px;
                }
              }
            }
          }
        }
      }

      [data-title='foot'] {
        display: block;
        margin: 83px 0 0;
        @media ${mqLargeMin} {
          margin: 135px 0 0;
        }
        .swiper {
          z-index: 0;
          width: 100%;
        }
        .banner > .container {
          width: 100%;
          height: 740px;
        }
        .media {
          background-size: cover;
          position: absolute;
          height: 100%;
          width: 100%;
          left: 0;
          top: 0;
        }
        .text {
          ${WithSiteLayoutWidth}
          ${WithSiteLayoutPadding}
        display: flex;
          align-items: center;
          justify-content: flex-end;
          transform: translateX(-50%);
          position: absolute;
          height: 100%;
          width: 100%;
          left: 50%;
          top: 0;
          .container {
            max-width: 357px;
            display: flex;
            flex-direction: column;
          }
          .button {
            text-transform: none;
            margin: var(--spacing-24) 0 0;
            border-width: 1px;
            border-color: #fff;
            width: 206px;
            &:hover,
            &:focus {
              background: var(--scrim-glass-light-20);
            }
            * {
              font-size: var(--font-size-350);
              color: #fff;
            }
          }
          h3 {
            ${WithFontHead}
            color: #fff;
            font-size: var(--font-size-600);
            @media ${mqLargeMin} {
              font-size: var(--font-size-800);
            }
          }
        }
      }
    }

    .site-aside {
      @media ${mqLargeMin} {
        width: calc((1440px - 645px - 100px) / 2) !important;
      }
      > .head {
        > .title {
          margin: 0 0 1rem;
          @media ${mqLargeMin} {
            margin: 1rem 0;
          }
        }
        > .subtitle {
          margin: 40px 0 20px;
          * {
            font-weight: 400;
            font-size: var(--font-size-400);
          }
        }
      }
      .body {
        > .site-filter.site-filter-sm {
          .head {
            .title {
              min-width: 171px;
            }
            .tag {
              ${Ellipsis}
              max-width: 90px;
            }
          }
        }
        .filter-mobile {
          .site-filter.site-filter-sm {
            margin: 16px 0 36px;
            .tag {
              margin-left: 0;
            }
          }
        }
      }
      .foot {
        margin: 0 0 70px;
        display: block;
        @media ${mqLargeMin} {
          margin: 40px 0 0;
        }
      }
    }
  }
  .page-reference.with-scrolled,
  .page-new-sustainability.with-scrolled {
    .site-header {
      opacity: 0;
      @media ${mqLargeMin} {
        opacity: 1;
      }
    }

    header {
      display: none;
      @media ${mqLargeMin} {
        display: block;
      }
    }
  }
`;

export const PageNewSustainability = css`
  .page-new-sustainability {
    .hero-section {
      .container {
        width: 100%;
        max-height: 670px !important;
        div[data-type='poster'] {
          overflow: hidden;
        }
        .text {
          margin: 0 var(--spacing-24);
          @media ${mqLargeMin} {
            margin: 0 auto;
            max-width: 1440px;
            padding-left: 50px;
            padding-right: 50px;
          }
          p {
            @media ${mqLargeMin} {
              max-width: 350px;
            }
          }
        }
      }
    }

    .site-content {
      padding-top: 0 !important;

      .fixed-width {
        width: 645px !important;
      }

      section > .container.with-flex {
        display: flex;
        flex-wrap: wrap;
      }
      section > .container > * {
        width: 100%;
      }

      .card {
        width: 100%;
        display: flex;
        flex-direction: column;
        padding: 0;
        &:not(:first-child) {
          border-top: 1px dotted var(--outline-neutral);
          margin-top: 40px;
        }
        @media ${mqLargeMin} {
          margin-top: 50px;
          border: 0 !important;
          width: 48% !important;
          padding-right: var(--spacing-48);
          &:first-child,
          &:nth-child(2) {
            margin-bottom: 165px !important;
          }
        }
        > * {
          order: 2;
        }
        div[data-type='poster'] {
          order: 1;
          margin: 0 -30px;
          width: 100vw;
          @media ${mqLargeMin} {
            height: 300px;
            max-width: 470px;
          }
        }
        h3 {
          font-size: var(--font-size-400) !important;
          font-weight: 500 !important;
          margin-bottom: 0 !important;
          z-index: 4;
          position: relative;
        }
        p {
          margin-top: 0 !important;
        }
        .small {
          margin-top: 20px;
          * {
            font-size: var(--font-size-250);
            @media ${mqSmallMin} {
              font-size: 13px;
            }
          }
        }
      }
    }
    .s0 {
      height: 100vh;
      position: relative;
      @media ${mqSmallMin} {
        height: calc(750px - 80px);
      }
      * {
        color: #fff;
      }
      div[data-type='poster'] {
        position: absolute;
        top: 0;
        left: 0;
        align-items: stretch;
        height: 100%;
        width: 100%;
        * {
          width: 100%;
        }
        .title {
          ${WithFontHead}
          @media ${mqSmallMin} {
            font-size: var(--font-size-1200);
            width: 300px;
          }
        }
        .picture {
          float: left;
          overflow: hidden;
          position: relative;
          background-position: center;
          background-size: cover;
          height: 100%;
          width: 100%;
          & > div {
            height: 100%;
          }
          img {
            display: none !important;
          }
        }
        .grid {
          display: block;
          position: relative;
          margin: 0 -1px;
          height: 100%;
          > .container {
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            position: absolute;
          }
        }
      }
    }

    .s0 {
      display: flex;
      align-items: center;
      justify-content: left;
      margin: 0;
      @media ${mqSmallMin} {
        height: calc(750px - 80px);
      }
      > .container {
        height: 100%;
        display: flex;
        align-items: center;
      }

      .text {
        width: 350px;
        max-width: 100%;
        text-align: left;
        position: relative;
        z-index: 2;
        h1 {
          font-size: 40px;
          line-height: 1.2;
          margin-bottom: 20px;
          @media ${mqSmallMin} {
            font-size: 50px;
          }
        }
      }
    }
  }
`;
